import { Component, Injectable, Input, OnInit } from '@angular/core';
import { JobPointeService } from 'src/app/service/job-pointe/job-pointe.service';
import { MomentService } from 'src/app/service/shared/moment/moment.service';
import { JobPointeSearchParams } from 'src/app/shared/models/jobPointeSearchParams';
import { Router, ActivatedRoute } from '@angular/router';
import { DialogService } from 'src/app/service/dialog/dialog.service';
import { TableData } from 'src/app/shared/models/tableData';
import { JobPointeTransferService } from '../job-pointe-transfer/job-pointe-transfer.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { LoaderService } from 'src/app/service/shared/loader/loader.service';
import { JobBoardService } from '../job-board/service/job-board.service';
import { parseExportFileName } from 'src/app/helpers/parseExportFileName';
import type { SearchParams } from 'src/app/shared/types/SearchBarParams';
import Swal from 'sweetalert2';
import { SweetAlertService } from 'src/app/service/shared/toast/sweet-alert.service';

@Component({
  selector: 'app-job-pointe-list',
  templateUrl: './job-pointe-list.component.html',
  styleUrls: ['./job-pointe-list.component.scss'],
})
@Injectable({
  providedIn: 'root',
})
export class JobPointeListComponent implements OnInit {
  private refreshAllSource = new BehaviorSubject<any>({});
  refreshAll = this.refreshAllSource.asObservable();

  filterType;
  constructor(
    private jobPointeService: JobPointeService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialogService: DialogService,
    private jobPointeTransferService: JobPointeTransferService,
    private jobBoardService: JobBoardService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private momentService: MomentService,
    private sweetAlertService: SweetAlertService
  ) { }

  @Input() data: any;
  @Input() refreshList: Observable<any>;
  @Input() tableData: TableData;
  @Input() tableFilters: Array<any>;
  @Input() moreFilters: Array<any>;
  @Input() searchParams: JobPointeSearchParams = { page: 1, limit: 25 };
  @Input() jobPointeExportParams: JobPointeSearchParams = {};
  @Input() jobPointeModelExportParams: JobPointeSearchParams = {};
  @Input() jobPointeTransferExportParams: JobPointeSearchParams = {};
  @Input() source: string;
  @Input() modelId: number;
  @Input() hideTableBar: Boolean;
  @Input() hideTablePaging: Boolean;
  @Input() hasCount: Boolean;

  modelDetail: any = {
    'asset-classes': {},
    categories: {},
    manufacturers: {},
  };
  dialogRecord: any = {};
  endpoint: string;
  selectedItems: Array<any> = [];
  filterData: Array<any> = [];
  subscription: Subscription;
  actionSource: string;
  refreshSubscription: Subscription;
  selectedItemsSubscription: Subscription;

  ngOnInit(): void {
    if (this.hasCount) this.getCount();

    if (this.data) {
      this.tableData.data = this.data;
      return;
    }

    if (this.tableData.type === 'employees' || this.tableData.type === 'jobBoardEmployees') {
      this.searchParams['scope[status]'] = '1';
    }

    this.filterType =
      this.source === 'employees'
        ? 'employeeFilter'
        : this.source === 'configurations'
          ? 'configurationFilter'
          : 'transferFilter';

    this.selectedItemsSubscription = this.jobPointeTransferService.selectedItems.subscribe(data => {
      this.selectedItems = data;
    });

    if (this.refreshList) {
      this.refreshList.subscribe(res => {
        if (!res) return;
        this.getData();
      });
    }

    this.jobPointeTransferService.refreshData = this.getData.bind(this);
  }

  updateFilters(filterData) {
    this.tableFilters.map(filter => {
      filter.default = filterData[filter.propertyName] ? filterData[filter.propertyName] : '';
    });
  }

  ngAfterViewInit() {
    this.searchParams.searchFields = this.searchFields;
    if (this.data) return;
    setTimeout(() => {
      this.jobPointeService.setTotalCountLabel(this.tableData.type);
    });
  }

  ngOnDestroy() {
    if (this.refreshSubscription) this.refreshSubscription.unsubscribe();
    if (this.selectedItemsSubscription) this.selectedItemsSubscription.unsubscribe();
    this.jobPointeTransferService.clearSelectedItems();
  }

  getData(loader = true) {
    loader ? this.loaderService.displayLoader() : null;

    const params = {
      ...this.searchParams,
    };

    this.jobPointeService.read[this.tableData.type](params).subscribe(
      res => {
        this.loaderService.hideLoader();

        const data = res.data ? res.data : res;
        const content = this.tableData.type === 'employees' ? data : this.mapTableData(data);

        this.tableData.data = content;
        this.tableData.pagination = res.pagination;
        this.tableData.meta = res.meta;
      },
      error => {
        this.loaderService.hideLoader();
        console.error(error);
      },
    );
  }

  getCount() {
    this.jobPointeService.count[this.tableData.type]({}).subscribe(
      res => {
        this.jobPointeService.setTotalCount(res.data.count);
      },
      error => {
        console.error(error);
      },
    );
  }

  handleAction(actionData): void {
    const { action, payload, source, actionPayload } = actionData;
    this.actionSource = source;

    if (actionPayload) {
      this.actions[action]({ row: payload, actionPayload });
      return
    }

    this.actions[action](payload);
  }

  actions = {
    edit: record => this.performEdit(record),
    delete: record => this.performDelete(record),
    status: record => this.performStatusChange(record),
    sort: sortData => this.performSorting(sortData),
    transfer: record => this.performTransfer(record),
    add: record => this.performAdd(record),
    addEmployeeToCard: ({ row: employee, actionPayload: cardData }) =>
      this.performAddEmployeeToCard(employee, cardData),
    openEmpProfilePopup: record => this.jobPointeTransferService.selectedRowSource.next(record),
    openJobBoardAssignmentCard: record => this.jobPointeTransferService.jobCardModalIsOpenSource.next(record),
    addJobs: jobs => this.performBatchAddJobsToJobBoard(jobs),
    activateJobs: jobs => this.performBatchStatusUpdate(jobs, 'Active'),
    deactivateJobs: async jobs => this.performBatchStatusUpdate(jobs, 'Inactive'),
    holdJobs: async jobs => this.performBatchStatusUpdate(jobs, 'Hold'),
    archiveEmployeeScheduleHistory: record => this.performArchiveEmployeeScheduleHistory(record)
  };

  async performAddEmployeeToCard(employee, cardData) {
    try {
      employee.addingUser = true;
      const result = await this.jobPointeTransferService.addEmployeeToCard(employee, cardData, false, false);

      this.toastService.showToast(result.message, result.success ? 'success' : 'error');

      if (result.success) employee.hasBeenAddedSuccessfully = true


      this.getData(false);

      this.jobBoardService.updateJobBoard(false);
    } catch (error) {
      console.error(error);
    }
  }

  async performBatchStatusUpdate(jobs, newStatus) {
    try {
      await this.jobPointeTransferService.batchUpdateJobBoardStatus(jobs, newStatus);
      this.getData();
    } catch (error) {
      console.error(error);
    }
  }
  async performBatchAddJobsToJobBoard(jobs) {
    try {
      await this.jobPointeTransferService.batchAddJobsToJobBoard(jobs);
      this.getData();
    } catch (error) {
      console.error(error);
    }
  }

  performSearch(searchParams: SearchParams): void {
    this.searchParams = { ...this.searchParams, ...searchParams };
    this.searchParams.page = 1;
    this.jobPointeExportParams = Object.assign(this.jobPointeExportParams, this.searchParams);

    this.jobPointeModelExportParams = Object.assign(this.jobPointeModelExportParams, this.searchParams);
    this.jobPointeTransferExportParams = Object.assign(this.jobPointeTransferExportParams, this.searchParams);

    this.getData();
  }

  performSorting(sortData: any) {
    this.searchParams.order = sortData;
    //Add scope for only fetching tools associated to a model on tool-model-page
    if (this.actionSource === 'models' && this.modelId) {
      this.searchParams['scope[modelTools]'] = this.modelId;
    }
    this.getData();
  }

  performPaging(pageLink: string): void {
    if (parseInt(pageLink)) {
      this.searchParams.page = parseInt(pageLink);
    }
    this.getData();
  }

  performClear() {
    const { limit, searchFields } = this.searchParams

    this.searchParams = {
      limit,
      searchFields,
      ['scope[status]']: 1,
      page: 1
    }
    delete this.searchParams.order;
  }

  async performArchiveEmployeeScheduleHistory(record: { id: number; userId: number }) {
    const { id, userId } = record;

    if (!id || !userId) return;

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently delete the job history entry.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it',
      reverseButtons: true,
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary'
      }
    });

    // If user confirms deletion
    if (result.isConfirmed) {
      this.jobPointeService.update.jobHistory({ historyId: id, userId }).subscribe(
        () => {
          this.toastService.showToast('Successfully removed job history.', 'success');
          this.getTableData();
        },
        err => {
          this.toastService.showToast('There was a problem removing job history.', 'error');
          console.error('error:', err);
        }
      );
    }
  }

  refreshDataForSelectAll(dataLimit: number = 100000) {
    this.searchParams = Object.assign(this.searchParams, this.filterData);
    this.searchParams.page = 1;
    this.searchParams.limit = dataLimit;
    this.searchParams = this.setFilterParams(this.searchParams);
    this.getTableData(true);
  }

  mapTableData(data) {
    return data.map(el => {
      if (!el.potentialJobDetail) return el;
      const detail = el.potentialJobDetail.potentialJobs;
      const isArray = Array.isArray(detail);

      if (isArray) return el;

      const potentialJobDetail = { ...el.potentialJobDetail, potentialJobs: [el.potentialJobDetail.potentialJobs] };

      return { ...el, potentialJobDetail };
    });
  }

  getTableData(fetchAll: Boolean = false) {
    this.loaderService.displayLoader();

    const params = { ...this.searchParams };

    this.jobPointeService.read[this.tableData.type](params).subscribe(
      res => {
        this.loaderService.hideLoader();

        const data = res.data ? res.data : res;
        const content = this.tableData.type === 'employees' ? data : this.mapTableData(data);

        this.tableData.data = content;
        this.tableData.pagination = res.pagination;
        this.tableData.meta = res.meta;
        if (fetchAll) this.refreshAllSource.next(res);
      },
      err => {
        this.loaderService.hideLoader();
        console.error(err);
      },
    );
  }

  get searchFields() {
    const pageSearchFields = {
      employees: [
        'firstName',
        'lastName',
        'employeeAlias',
        'city',
        'employeeTitle.name',
        'employeeUnionLocal.name',
        'employeeDepartment.department_name',
        // NOTE: For searching current assignments.
        'jobBoardJobs.job.job_number',
        'jobBoardJobs.job.name',
        'jobBoardJobs.facility.owner.owner_name',
        'jobBoardJobs.facility.facility_name',
        // NOTE: End
      ],
      jobBoardEmployees: [
        'firstName',
        'lastName',
        'employeeAlias',
        'city',
        'title',
        'employeeDepartment.department_name',
      ],
      manageJobs: [
        'potentialJobDetail.name',
        'potentialJobDetail.facility.facility_name',
        'potentialJobDetail.owner.owner_name',
        'potentialJobDetail.number',
        'departmentNumber',
        'projectManager',
        'assistant',
        'foreman',
      ],
    };
    const fields = pageSearchFields[this.tableData.type];
    return fields ? fields.join(',') : '';
  }

  setFilterParams(params) {
    const filterParams = Object.keys(params).reduce((filters, key) => {
      filters[key] = params[key];
      // remove empty scopes
      if (key.indexOf('scope') >= 0 && (!filters[key] || filters[key] === '')) delete filters[key];
      return filters;
    }, {});
    return filterParams;
  }

  performFilter(filterData) {
    this.filterData = filterData;
    // method used to add new filtered data and clean up old keys missing data
    this.searchParams = Object.assign(this.searchParams, filterData);
    this.searchParams.page = 1;
    this.jobPointeExportParams = Object.assign(this.jobPointeExportParams, filterData);
    this.jobPointeModelExportParams = Object.assign(this.jobPointeModelExportParams, filterData);
    this.jobPointeTransferExportParams = Object.assign(this.jobPointeTransferExportParams, filterData);
    this.searchParams = this.setFilterParams(this.searchParams);
    this.jobPointeExportParams = this.setFilterParams(this.jobPointeExportParams);
    this.jobPointeModelExportParams = this.setFilterParams(this.jobPointeModelExportParams);
    this.jobPointeTransferExportParams = this.setFilterParams(this.jobPointeTransferExportParams);

    this.getTableData();
  }

  performStatusChange(record): void {
    const { id, name, description, enabled } = record;
    const payload = { id, data: { name, description, enabled } };
    payload.data.enabled = !payload.data.enabled;
    this.jobPointeService.status[this.tableData.type](payload).subscribe(
      res => {
        this.getData();
        this.toastService.showToast('Successfully updated status', 'success');
      },
      err => {
        this.toastService.showToast('Error in updating status', 'error');
        this.getData();
        console.error('error:', err);
      },
    );
  }

  createTool(): void {
    if (this.source === 'models') {
      this.bindTool();
    }
    this.jobPointeService.create[this.tableData.type]().subscribe(
      res => {
        this.router.navigate(['./add/', res.data.id], {
          relativeTo: this.activatedRoute,
        });
      },
      err => {
        this.toastService.showToast('Failed to create a new tool record', 'error');
        console.error('error:', err);
      },
    );
  }

  createModel() {
    this.jobPointeService.create['models']().subscribe(
      res => {
        this.router.navigate(['./add/', res.data.id], {
          relativeTo: this.activatedRoute,
        });
      },
      err => {
        this.toastService.showToast('Failed to create a new model record', 'error');
        console.error('error:', err);
      },
    );
  }

  performAdd(payload: any): void {
    this.jobPointeService.create['tools']().subscribe(
      res => {
        this.updateTools(res.data.id, payload);
      },
      err => {
        console.error('error:', err);
      },
    );
  }

  updateTools(toolId, payload) {
    const createToolPayload = {
      id: toolId,
      data: {
        toolModelId: payload.id,
        inspectionRequired: payload.inspectionRequired,
        maintenanceRequired: payload.maintenanceRequired,
      },
    };
    this.jobPointeService.update['tools'](createToolPayload).subscribe(() => {
      this.router.navigate(['/tools/edit/', toolId], {
        relativeTo: this.activatedRoute,
      });
    }),
      err => {
        console.error('error:', err);
      };
  }

  bindTool() {
    this.jobPointeService.create['tools']().subscribe(res => {
      const createToolPayload = {
        id: res.data.id,
        data: {
          toolModelId: this.modelId,
        },
      };
      this.updateTool(createToolPayload);
    });
  }

  updateTool(payload) {
    this.jobPointeService.update['tools'](payload).subscribe(result => {
      this.router.navigate(['/tools/edit/', payload.id], {
        relativeTo: this.activatedRoute,
      });
    }),
      err => {
        console.error('error:', err);
      };
  }

  performCreate(type: string): void {
    if (this.tableData.type === 'tools') this.createTool();
    else if (this.tableData.type === 'models') this.createModel();
  }

  performEdit(record: any): void {
    this.router.navigate(['./edit/', record.id], {
      relativeTo: this.activatedRoute,
    });
  }

  performDelete(record): void {
    this.dialogRecord.actionType = 'delete';
    this.dialogRecord.actionName = 'Delete';
    if (this.tableData.type === 'tools') {
      let toolId = record.toolId ? record.toolId : record.legacyToolId;
      let desc = record.description ? `: ${record.description}` : '';
      this.dialogRecord.name = `${toolId}${desc}`;
    } else if ((this.tableData.type = 'models')) {
      this.dialogRecord.name = record.name;
    }
    this.dialogRecord.data = record;

    this.dialogService.open('delete-dialog').then(response => {
      // if user decided to continue, proceed with change
      if (response) {
        this.jobPointeService.delete[this.tableData.type](record.id).subscribe(
          res => {
            this.toastService.showToast('Deleted successfully', 'success');
            this.getData();
            this.getCount();
            if (this.tableData.type === 'tools' && this.jobPointeTransferService.get(record.id)) {
              this.jobPointeTransferService.remove(record.id);
            }
          },
          err => {
            if (err.status === 423) {
              this.toastService.showToast(err.error.errors[0].error, 'error');
            } else {
              this.toastService.showToast('Failed to delete record', 'error');
            }
            this.getData();
          },
        );
      }
    });
  }

  handleSelectAll(items: any) {
    this.selectedItems = items;
    this.jobPointeTransferService.batchAdd(items);
  }

  handleRemoveAll(items: any) {
    this.selectedItems = [];
    this.jobPointeTransferService.clearSelectedItems();
  }

  handleSelectedAdd(id: number) {
    const item = this.tableData.data.find(row => row.id == id);
    this.jobPointeTransferService.add(item);
  }

  handleSelectedRemove(id: number) {
    this.jobPointeTransferService.remove(id);
  }

  performTransfer(record: any): void {
    //Refresh tools after successful tool transfer
    this.refreshSubscription = this.jobPointeTransferService.refreshToolsSubscriber.subscribe(res => {
      if (res.next && !this.refreshSubscription?.closed) {
        this.getTableData();
      }
    });
    if (record) {
      this.jobPointeTransferService.transfer(record, this.refreshSubscription);
    } else {
      this.jobPointeTransferService.batchTransfer(this.refreshSubscription);
    }
  }

  handleSelectedChange(records: any[]) {
    this.selectedItems = records;
  }

  getFileType = (url: string) => {
    const params = new URL(url).searchParams;
    const exportTypeParam = params.get('exportType');
    const exportType = exportTypeParam !== 'default' ? exportTypeParam : 'Employees';
    return parseExportFileName(exportType);
  };

  performEmployeeBatchExport(employeeExportOptions = {}): void {
    // immutably remove limit and page
    const { limit, page, attributes, ...exportParams } = this.jobPointeExportParams;

    this.jobPointeService.export['employees']({
      ...exportParams,
      ...employeeExportOptions,
    }).subscribe(
      res => {
        this.loaderService.hideLoader();
        const fileName = this.getFileType(res.url);

        this.downloadFile(res.body, fileName);
      },
      err => {
        this.loaderService.hideLoader();
        console.error(err);
      },
    );
  }

  downloadFile(blob: any, fileType: String) {
    const a = document.createElement('a');
    const currentDate = this.momentService.getCurrentDate('MMDDYYYY');
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = `${currentDate}_${fileType}_export.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  loadSelectedItems(): Promise<void> {
    return Promise.resolve().then(() => {
      this.subscription = this.jobPointeTransferService.selectedItems.subscribe(
        data => {
          this.selectedItems = data;
        },
        e => console.error('Transfer service error', e),
      );
    });
  }
}
